import type { FC } from "react"
import type { ChannelType } from "./contactChannels"

interface ContactChannelGlyphProps {
  channelType: ChannelType
  /** CMS-uploaded icon URL. When present it overrides the predefined glyph. */
  iconUrl?: string | null
  /** Square render size in px. */
  size?: number
  alt?: string
}

/**
 * Renders the icon for a contact channel. If the CMS editor uploaded an icon it
 * is used as-is; otherwise a predefined brand glyph is drawn from the channelType
 * (WhatsApp green, Messenger blue, Phone orange). This keeps both the global
 * Contact modal and the standalone Contact Channels block looking correct with no
 * required media uploads, while still allowing per-channel overrides.
 */
const ContactChannelGlyph: FC<ContactChannelGlyphProps> = ({
  channelType,
  iconUrl,
  size = 56,
  alt = "",
}) => {
  if (iconUrl) {
    return (
      <img
        src={iconUrl}
        alt={alt}
        width={size}
        height={size}
        style={{ objectFit: "contain", flexShrink: 0 }}
      />
    )
  }

  if (channelType === "whatsapp") return <WhatsAppGlyph size={size} />
  if (channelType === "phone") return <PhoneGlyph size={size} />
  return <MessengerGlyph size={size} />
}

export default ContactChannelGlyph

const WhatsAppGlyph: FC<{ size: number }> = ({ size }) => (
  // EGDE-368: brand glyph sits in a circle (Figma), not a rounded square.
  <svg
    width={size}
    height={size}
    viewBox="0 0 56 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    style={{ flexShrink: 0 }}
  >
    <circle cx="28" cy="28" r="28" fill="#25D366" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M28 13c-8.28 0-15 6.72-15 15 0 2.93.84 5.66 2.31 7.97L13 43l7.22-2.27A14.9 14.9 0 0 0 28 42.87c8.28 0 15-6.72 15-14.94C43 19.72 36.28 13 28 13Zm-4.56 7.75c-.28-.66-.6-.62-1-.62-.34 0-.81.12-1.25.59-.44.47-1.69 1.65-1.69 4.06s1.72 4.72 1.96 5.06c.25.34 3.34 5.37 8.16 7.21 3.84 1.47 4.78 1.13 5.64 1.04.88-.09 2.88-1.24 3.25-2.42.38-1.19.38-2.21.26-2.42-.13-.21-.47-.34-1-.56-.44-.22-2.81-1.4-3.22-1.55-.41-.16-.69-.22-.97.22-.28.44-1.19 1.43-1.44 1.71-.25.28-.5.31-.94.09-.44-.22-2.03-.78-3.91-2.43-1.47-1.31-2.47-2.94-2.69-3.44-.25-.43-.03-.69.19-.91.2-.2.44-.5.66-.78.22-.25.31-.44.47-.72.13-.28.06-.53-.06-.75-.13-.22-.97-2.66-1.62-3.66Z"
      fill="#fff"
    />
  </svg>
)

const MessengerGlyph: FC<{ size: number }> = ({ size }) => (
  // EGDE-368: solid azure circle + Messenger bolt. Colour #00A6FF sampled
  // directly from the Figma node (was #0A7CFF — too royal/indigo).
  <svg
    width={size}
    height={size}
    viewBox="0 0 56 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    style={{ flexShrink: 0 }}
  >
    <circle cx="28" cy="28" r="28" fill="#00A6FF" />
    {/* EGDE-368: bolt path bbox was centred at (31.5, 25.75); the circle
        centre is (28, 28). Offset it so the Messenger bolt sits centred. */}
    <path
      transform="translate(-3.5 2.25)"
      d="M14.7 32.86 22.92 19.8c1.3-2.07 4.1-2.58 6.04-1.12l6.53 4.9c.6.45 1.42.45 2.03 0l8.82-6.7c1.18-.9 2.72.51 1.94 1.75L40.1 31.7c-1.3 2.07-4.1 2.58-6.04 1.12l-6.53-4.9a1.54 1.54 0 0 0-2.03 0l-8.82 6.7c-1.18.9-2.72-.51-1.94-1.75Z"
      fill="#fff"
    />
  </svg>
)

const PhoneGlyph: FC<{ size: number }> = ({ size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 56 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    style={{ flexShrink: 0 }}
  >
    {/* EGDE-368: exact Iconsax "call-calling" glyph, extracted vector-for-vector
        from the Figma node — solid handset + two incoming-call arcs at the
        top-end corner. Transforms reproduce Figma's sub-layer positions. */}
    <g transform="translate(3.915 3.914)">
      <path
        d="M47.3467 38.8533C47.3467 39.6933 47.16 40.5567 46.7633 41.3967C46.3667 42.2367 45.8533 43.03 45.1767 43.7767C44.0333 45.0367 42.7733 45.9467 41.35 46.53C39.95 47.1133 38.4333 47.4167 36.8 47.4167C34.42 47.4167 31.8767 46.8567 29.1933 45.7133C26.51 44.57 23.8267 43.03 21.1667 41.0933C18.4833 39.1333 15.94 36.9633 13.5133 34.56C11.11 32.1333 8.94 29.59 7.00333 26.93C5.09 24.27 3.55 21.61 2.43 18.9733C1.31 16.3133 0.75 13.77 0.75 11.3433C0.75 9.75667 1.03 8.24 1.59 6.84C2.15 5.41667 3.03667 4.11 4.27333 2.94333C5.76667 1.47333 7.4 0.75 9.12667 0.75C9.78 0.75 10.4333 0.89 11.0167 1.17C11.6233 1.45 12.16 1.87 12.58 2.47667L17.9933 10.1067C18.4133 10.69 18.7167 11.2267 18.9267 11.74C19.1367 12.23 19.2533 12.72 19.2533 13.1633C19.2533 13.7233 19.09 14.2833 18.7633 14.82C18.46 15.3567 18.0167 15.9167 17.4567 16.4767L15.6833 18.32C15.4267 18.5767 15.31 18.88 15.31 19.2533C15.31 19.44 15.3333 19.6033 15.38 19.79C15.45 19.9767 15.52 20.1167 15.5667 20.2567C15.9867 21.0267 16.71 22.03 17.7367 23.2433C18.7867 24.4567 19.9067 25.6933 21.12 26.93C22.38 28.1667 23.5933 29.31 24.83 30.36C26.0433 31.3867 27.0467 32.0867 27.84 32.5067C27.9567 32.5533 28.0967 32.6233 28.26 32.6933C28.4467 32.7633 28.6333 32.7867 28.8433 32.7867C29.24 32.7867 29.5433 32.6467 29.8 32.39L31.5733 30.64C32.1567 30.0567 32.7167 29.6133 33.2533 29.3333C33.79 29.0067 34.3267 28.8433 34.91 28.8433C35.3533 28.8433 35.82 28.9367 36.3333 29.1467C36.8467 29.3567 37.3833 29.66 37.9667 30.0567L45.69 35.54C46.2967 35.96 46.7167 36.45 46.9733 37.0333C47.2067 37.6167 47.3467 38.2 47.3467 38.8533Z"
        fill="#F86F3E"
        stroke="#F86F3E"
        strokeWidth="1.5"
        strokeMiterlimit="10"
      />
    </g>
    <path
      transform="translate(34.25 12.085)"
      d="M8.91667 8.91667C8.91667 7.51667 7.82 5.37 6.18667 3.62C4.69333 2.01 2.71 0.75 0.75 0.75"
      stroke="#F86F3E"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      transform="translate(34.25 3.915)"
      d="M17.0833 17.0833C17.0833 8.05333 9.78 0.75 0.75 0.75"
      stroke="#F86F3E"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
